import {applyFitLevel, fitToHeight} from '@chekinapp/ui';
import {VelaScene, type VelaCardSection, type VelaSectionId} from '../engine/types';

export const VELA_BASE_FIT_LEVELS = 3;
export const PHOTO_TIPS_COMPACT_LEVEL = 4;
export const QR_MAX_SIZE = 120;
export const QR_MIN_SIZE = 24;
export const QR_SIZE_STEP = 4;
export const QR_SIZE_VAR = '--vela-qr-size';

export type VelaFitSceneConfig = {
  levels?: number;
  dropSections?: VelaSectionId[];
  keepFirstInSection?: boolean;
};

const ASK_VELA_FIT: VelaFitSceneConfig = {
  dropSections: ['askVela'],
  keepFirstInSection: true,
};

const IV_PHOTO_FIT: VelaFitSceneConfig = {levels: PHOTO_TIPS_COMPACT_LEVEL};

export const VELA_FIT_SCENES: Partial<Record<VelaScene, VelaFitSceneConfig>> = {
  [VelaScene.GuestRegistration]: ASK_VELA_FIT,
  [VelaScene.GuestForm]: ASK_VELA_FIT,
  [VelaScene.IVFront]: IV_PHOTO_FIT,
  [VelaScene.IVBack]: IV_PHOTO_FIT,
  [VelaScene.IVSelfie]: IV_PHOTO_FIT,
};

const HIDDEN_AT_LEVEL: Record<number, string> = {
  4: "[[data-fit~='4']_&]:hidden",
  5: "[[data-fit~='5']_&]:hidden",
  6: "[[data-fit~='6']_&]:hidden",
  7: "[[data-fit~='7']_&]:hidden",
  8: "[[data-fit~='8']_&]:hidden",
  9: "[[data-fit~='9']_&]:hidden",
  10: "[[data-fit~='10']_&]:hidden",
};

const MAX_DROP_LEVEL = 10;

export function fitHiddenClass(level: number | undefined) {
  return level === undefined ? undefined : HIDDEN_AT_LEVEL[level];
}

export type VelaFitPlan = {
  levels: number;
  cardDropLevels: Map<string, number>;
  sectionDropLevels: Map<VelaSectionId, number>;
};

export function getVelaFitPlan(
  scene: VelaScene,
  sections: VelaCardSection[],
): VelaFitPlan {
  const config = VELA_FIT_SCENES[scene] ?? {};
  const baseLevels = config.levels ?? VELA_BASE_FIT_LEVELS;
  const dropSections = new Set(config.dropSections ?? []);

  const droppable = sections
    .filter(section => dropSections.has(section.id))
    .flatMap(section =>
      section.cards
        .map((card, order) => ({section: section.id, card, order}))
        .filter(({order}) => !(config.keepFirstInSection && order === 0)),
    )
    .sort((a, b) => (a.card.priority ?? 0) - (b.card.priority ?? 0) || b.order - a.order)
    .slice(0, MAX_DROP_LEVEL - baseLevels);

  const cardDropLevels = new Map(
    droppable.map(({card}, index) => [card.id, baseLevels + index + 1]),
  );

  const sectionDropLevels = new Map<VelaSectionId, number>();
  for (const section of sections) {
    const levels = section.cards.map(card => cardDropLevels.get(card.id));
    if (!levels.length || levels.some(level => level === undefined)) continue;
    sectionDropLevels.set(section.id, Math.max(...(levels as number[])));
  }

  return {
    levels: baseLevels + droppable.length,
    cardDropLevels,
    sectionDropLevels,
  };
}

const fits = (element: HTMLElement) => element.scrollHeight <= element.clientHeight + 1;

function shrinkQrToFit(element: HTMLElement, qr: HTMLElement) {
  let size = QR_MAX_SIZE;
  qr.style.setProperty(QR_SIZE_VAR, `${size}px`);
  while (!fits(element) && size > QR_MIN_SIZE) {
    size -= QR_SIZE_STEP;
    qr.style.setProperty(QR_SIZE_VAR, `${size}px`);
  }

  return fits(element);
}

export function fitQrFirst(element: HTMLElement, levels: number) {
  const qr = element.querySelector<HTMLElement>('[data-vela-qr]');
  if (!qr) return;

  applyFitLevel(element, 0);
  delete element.dataset.fitOverflow;
  if (shrinkQrToFit(element, qr)) return;

  fitToHeight(element, levels);
}
